/**
 * ExportButton — icon button with a two-option dropdown (Current view / Full report).
 *
 * Used twice in the filter band: once for Print (PrinterIcon), once for Excel
 * (MsExcel branded SVG). Each option calls back with a scope key the consumer
 * uses to assemble the right dataset + columns.
 *
 * Scope semantics:
 *   - 'current' = WYSIWYG. Filtered rows, current sort, visible columns only.
 *   - 'full'    = all data, all columns. Ignores Status/Role filters AND
 *                 column hides. Always renders every column the view defines.
 */
import React, { useState } from 'react';
import { Box, ChevronDownIcon, HStack, Popover, Pressable, VStack } from 'native-base';
import { Text } from '@pimm/base';

export type ExportScope = 'current' | 'full';

type Props = {
  icon: React.ReactNode;
  buttonLabel: string;
  accessibilityLabel: string;
  currentViewLabel: string;
  fullReportLabel: string;
  onSelect: (scope: ExportScope) => void;
};

export const ExportButton: React.FC<Props> = ({ icon, buttonLabel, accessibilityLabel, currentViewLabel, fullReportLabel, onSelect }) => {
  // Controlled open state — native-base's Popover only auto-dismisses on a
  // backdrop press/Escape by default; single-select actions like this should
  // close immediately on selection.
  const [isOpen, setIsOpen] = useState(false);
  const handleSelect = (scope: ExportScope) => {
    setIsOpen(false);
    onSelect(scope);
  };
  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      trigger={triggerProps => (
        <Pressable {...triggerProps} onPress={() => setIsOpen(!isOpen)} accessibilityLabel={accessibilityLabel} _hover={{ bg: 'gray.50' }}>
          <HStack
            alignItems="center"
            justifyContent="center"
            space={1.5}
            px={3}
            h={9}
            bg="white"
            borderWidth={1}
            borderColor="gray.300"
            rounded="lg"
            shadow={1}
          >
            {icon}
            <Text size="sm" fontWeight={500} color="gray.700">
              {buttonLabel}
            </Text>
            <ChevronDownIcon size={3} color="gray.500" />
          </HStack>
        </Pressable>
      )}
      placement="bottom right"
    >
      <Popover.Content w={220} borderWidth={0} shadow="7">
        <Popover.Body p={0} bg="white">
          <VStack py={1}>
            {[
              { key: 'current' as const, label: currentViewLabel },
              { key: 'full' as const, label: fullReportLabel },
            ].map(opt => (
              <Pressable key={opt.key} onPress={() => handleSelect(opt.key)} _hover={{ bg: 'gray.50' }}>
                <Box px={4} minH={9} justifyContent="center">
                  <Text size="md" fontWeight={600} color="gray.800">
                    {opt.label}
                  </Text>
                </Box>
              </Pressable>
            ))}
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
